import React, { useState, useEffect } from 'react';
import { Search, Plus, X, Building2 } from 'lucide-react';
import useClickOutside from '../hooks/useClickOutside.js';
import { buildShippingSnapshot, formatAddressFields } from '../utils/shippingSnapshot.js';

const MocDeliveryDetails = ({
  formData,
  setFormData,
  validationErrors = {},
  setValidationErrors,
  allCustomers,
  allWarehouses,
  selectedCustomer,
  setSelectedCustomer,
  selectedWarehouse,
  setSelectedWarehouse,
  setShowNewCustomerModal,
  setShowNewWarehouseModal,
  setShowAddAddressModal,
  setAddressModalType,
  setAddressDataToEdit,
  sectionTitle = "Consignee & Delivery Details"
}) => {
  const typeOfDoc = formData.typeOfMOC;

  const clearValidationError = (fieldName) => {
    if (!setValidationErrors) return;
    setValidationErrors(prev => {
      if (!prev[fieldName]) return prev;
      const nextErrors = { ...prev };
      delete nextErrors[fieldName];
      return nextErrors;
    });
  };

  const getFieldErrorStyle = (fieldName, baseStyle = inputStyle) => (
    validationErrors[fieldName]
      ? { ...baseStyle, border: '1px solid #dc2626', boxShadow: '0 0 0 1px #dc2626' }
      : baseStyle
  );

  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const customerSearchRef = useClickOutside(() => setShowCustomerSuggestions(false));

  const [warehouseSearchTerm, setWarehouseSearchTerm] = useState('');
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);
  const [showWarehouseSuggestions, setShowWarehouseSuggestions] = useState(false);
  const warehouseSearchRef = useClickOutside(() => setShowWarehouseSuggestions(false));

  const [destWarehouseSearchTerm, setDestWarehouseSearchTerm] = useState('');
  const [filteredDestWarehouses, setFilteredDestWarehouses] = useState([]);
  const [showDestWarehouseSuggestions, setShowDestWarehouseSuggestions] = useState(false);
  const destWarehouseSearchRef = useClickOutside(() => setShowDestWarehouseSuggestions(false));

  // Sync terms with selected objects
  useEffect(() => {
    if (selectedCustomer) setCustomerSearchTerm(selectedCustomer.display_name || '');
    else setCustomerSearchTerm(formData.customerName || '');
  }, [selectedCustomer, formData.customerName]);

  useEffect(() => {
    if (selectedWarehouse) setWarehouseSearchTerm(selectedWarehouse.name || '');
    else setWarehouseSearchTerm(formData.warehouse || '');
  }, [selectedWarehouse, formData.warehouse]);

  useEffect(() => {
    if (typeOfDoc === 'Interbranch Transfer' && formData.customerName) {
        setDestWarehouseSearchTerm(formData.customerName);
    }
  }, [typeOfDoc, formData.customerName]);

  const formatAddress = (addr) => {
    if (!addr) return '';
    if (typeof addr === 'string') return addr;
    return formatAddressFields(addr);
  };

  const updateSalesShippingSnapshot = (prev, overrides = {}) => {
    const next = { ...prev, ...overrides };
    if (typeOfDoc !== 'Sales' && typeOfDoc !== 'Rental') {
      return next;
    }

    return {
      ...next,
      shippingSnapshot: buildShippingSnapshot({
        customerName: next.customerName,
        address: {
          id: next.shippingAddressId,
          address: next.shippingSnapshot?.address || '',
          city: next.shippingSnapshot?.city || '',
          state: next.shippingSnapshot?.state || '',
          pincode: next.shippingSnapshot?.pincode || '',
          country: next.shippingSnapshot?.country || 'India',
          fullAddress: next.shippingAddress
        },
        addressId: next.shippingAddressId,
        contactName: next.shippingPersonName,
        contactPhone: next.shippingPersonPhone,
        contactEmail: next.shippingPersonEmail
      })
    };
  };

  const handleCustomerSearch = (value) => {
    clearValidationError('customerName');
    setCustomerSearchTerm(value);
    if (value.length > 0) {
      const term = value.toLowerCase();
      const matches = allCustomers.filter(c => c.display_name?.toLowerCase().includes(term));
      setFilteredCustomers(matches);
      setShowCustomerSuggestions(true);
    } else {
      setFilteredCustomers(allCustomers);
      setShowCustomerSuggestions(true);
    }
  };

  const selectCustomer = (customer) => {
    clearValidationError('customerName');
    setSelectedCustomer(customer);
    setCustomerSearchTerm(customer.display_name);
    setShowCustomerSuggestions(false);
    const firstShipping = customer.addresses?.find(a => a.address_type === 'Shipping');
    setFormData(prev => ({ 
        ...prev, 
        customerName: customer.display_name, 
        customerID: customer.id,
        shippingAddressId: firstShipping?.id || '',
        shippingAddress: firstShipping ? formatAddress(firstShipping) : '',
        shippingPersonName: firstShipping?.attention || '',
        shippingPersonPhone: firstShipping?.phone || '',
        shippingPersonEmail: firstShipping?.email || '',
        shippingSnapshot: buildShippingSnapshot({
          customerName: customer.display_name,
          address: firstShipping || null,
          addressId: firstShipping?.id || ''
        })
    }));
  };

  const handleWarehouseSearch = (value) => {
    clearValidationError('warehouse');
    setWarehouseSearchTerm(value);
    if (value.length > 0) {
      const term = value.toLowerCase();
      const matches = allWarehouses.filter(w => 
        w.name?.toLowerCase().includes(term) || 
        w.code?.toLowerCase().includes(term)
      );
      setFilteredWarehouses(matches);
      setShowWarehouseSuggestions(true);
    } else {
      setFilteredWarehouses(allWarehouses);
      setShowWarehouseSuggestions(true);
    }
  };

  const selectSourceWarehouse = (warehouse) => {
    clearValidationError('warehouse');
    setSelectedWarehouse(warehouse);
    setWarehouseSearchTerm(warehouse.name);
    const warehouseAddress = formatAddress(warehouse);
    const gstin = warehouse.gstin || warehouse.gst_number || '';
    setFormData(prev => ({ 
      ...prev, 
      warehouse: warehouse.name,
      sourceWarehouseID: warehouse.id,
      ...(isInterbranch ? {
        billToCustomer: warehouse.name || '',
        billToAddress: warehouseAddress,
        billToGstin: gstin,
        orderBy: warehouse.contactPerson || '',
        orderByContact: warehouse.phone || '',
        billToEmail: warehouse.email || ''
      } : {})
    }));
    setShowWarehouseSuggestions(false);
  };

  const handleDestWarehouseSearch = (value) => {
    clearValidationError('customerName');
    setDestWarehouseSearchTerm(value);
    if (value.length > 0) {
      const term = value.toLowerCase();
      const matches = allWarehouses.filter(w => 
        w.name?.toLowerCase().includes(term) || 
        w.code?.toLowerCase().includes(term)
      );
      setFilteredDestWarehouses(matches);
      setShowDestWarehouseSuggestions(true);
    } else {
      setFilteredDestWarehouses(allWarehouses);
      setShowDestWarehouseSuggestions(true);
    }
  };

  const selectDestWarehouse = (warehouse) => {
    clearValidationError('customerName');
    setDestWarehouseSearchTerm(warehouse.name);
    const warehouseAddress = formatAddress(warehouse);
    const gstin = warehouse.gstin || warehouse.gst_number || '';
    setFormData(prev => ({ 
      ...prev, 
      customerName: warehouse.name,
      shippingAddress: warehouseAddress,
      shippingPersonName: warehouse.contactPerson || '',
      shippingPersonPhone: warehouse.phone || '',
      shippingPersonEmail: warehouse.email || '',
      destinationWarehouseGstin: gstin
    }));
    setShowDestWarehouseSuggestions(false);
  };

  const handleAddressSelect = (addressId) => {
    if (addressId === 'new') {
      setAddressDataToEdit(null);
      setShowAddAddressModal(true);
    } else {
      const address = selectedCustomer.addresses?.find(a => a.id === addressId);
      if (address) {
        setFormData(prev => ({ 
          ...prev, 
          shippingAddressId: address.id || '',
          shippingAddress: formatAddress(address),
          shippingPersonName: address.attention || '',
          shippingPersonPhone: address.phone || '',
          shippingPersonEmail: address.email || '',
          shippingSnapshot: buildShippingSnapshot({
            customerName: prev.customerName,
            address,
            addressId: address.id || ''
          })
        }));
      }
    }
  };

  const isSalesRental = typeOfDoc === 'Sales' || typeOfDoc === 'Rental';
  const isInterbranch = typeOfDoc === 'Interbranch Transfer';
  const sourceColumnLabel = isInterbranch ? 'Bill To' : 'Source (Our Warehouse)';
  const destinationColumnLabel = isInterbranch ? 'Ship To' : `Destination (${isSalesRental ? 'Customer' : 'Deliver To'})`;

  return (
    <div style={cardStyle}>
      <h3 style={sectionHeaderStyle}>{sectionTitle}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
        
        {/* SOURCE COLUMN (ALWAYS WAREHOUSE) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div style={{ fontSize: '12px', fontWeight: '800', color: '#2563eb', textTransform: 'uppercase' }}>
            {sourceColumnLabel}
          </div>
          
          <div style={{ position: 'relative' }} ref={warehouseSearchRef}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '6px' }}>
            <label style={labelStyle}>Source Warehouse</label>
              {setShowNewWarehouseModal && (
                <button onClick={() => setShowNewWarehouseModal(true)} style={linkBtnStyle}>
                  <Plus size={14} /> New Warehouse
                </button>
              )}
            </div>
            <div style={getFieldErrorStyle('warehouse', { display: 'flex', alignItems: 'center', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0 10px' })}>
              <Building2 size={18} color="#64748b" />
              <input 
                type="text" 
                placeholder="Search warehouse..." 
                value={warehouseSearchTerm} 
                onChange={(e) => handleWarehouseSearch(e.target.value)} 
                onFocus={() => (warehouseSearchTerm.length > 0 || filteredWarehouses.length > 0) && setShowWarehouseSuggestions(true)}
                style={{ border: 'none', padding: '10px', outline: 'none', width: '100%', fontSize: '14px' }}
              />
              {warehouseSearchTerm && (
                <button onClick={() => { setSelectedWarehouse(null); setWarehouseSearchTerm(''); setFormData(prev => ({...prev, warehouse: '', sourceWarehouseID: '', billToCustomer: '', billToAddress: '', billToGstin: '', orderBy: '', orderByContact: '', billToEmail: ''})); }} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                  <X size={16} color="#64748b" />
                </button>
              )}
            </div>
            {showWarehouseSuggestions && (
              <div style={suggestionsBoxStyle}>
                {filteredWarehouses.length > 0 ? (
                  filteredWarehouses.map(wh => (
                    <div key={wh.id} onClick={() => selectSourceWarehouse(wh)} style={suggestionItemStyle}>
                      <div style={{ fontWeight: 'bold' }}>{wh.name} ({wh.code})</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{wh.city}, {wh.state}</div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '10px', textAlign: 'center', color: '#64748b' }}>No warehouses found</div>
                )}
              </div>
            )}
          </div>

          <div>
            <label style={labelStyle}>Source Warehouse Address</label>
            <div style={{ ...inputStyle, minHeight: '80px', backgroundColor: '#f9fafb', color: '#475569', fontSize: '13px', overflowY: 'auto' }}>
              {selectedWarehouse ? (
                <>
                  {selectedWarehouse.address}<br />
                  {selectedWarehouse.city}, {selectedWarehouse.state} - {selectedWarehouse.pincode}
                </>
              ) : (
                <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Select a warehouse to view address...</span>
              )}
            </div>
          </div>

          <div style={{ backgroundColor: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', minHeight: '80px' }}>
            <label style={labelStyle}>Source Contact Details</label>
            {selectedWarehouse ? (
              <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ fontWeight: '600', color: '#1e293b' }}>{selectedWarehouse.contactPerson || '—'}</div>
                <div style={{ color: '#475569' }}>Phone: {selectedWarehouse.phone || '—'}</div>
                <div style={{ color: '#475569' }}>Email: {selectedWarehouse.email || '—'}</div>
              </div>
            ) : (
              <div style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>Select source warehouse to see details...</div>
            )}
          </div>
        </div>

        {/* DESTINATION COLUMN */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div style={{ fontSize: '12px', fontWeight: '800', color: '#059669', textTransform: 'uppercase' }}>
            {destinationColumnLabel}
          </div>

          {isSalesRental ? (
            <div style={{ position: 'relative' }} ref={customerSearchRef}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '6px' }}>
                <label style={labelStyle}>Consignee (Customer Name)</label>
                <button onClick={() => setShowNewCustomerModal(true)} style={linkBtnStyle}>
                  <Plus size={14} /> New Customer
                </button>
              </div>
              <div style={getFieldErrorStyle('customerName', { display: 'flex', alignItems: 'center', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0 10px' })}>
                <Search size={18} color="#64748b" />
                <input 
                  type="text" 
                  placeholder="Search customer..." 
                  value={customerSearchTerm} 
                  onChange={(e) => handleCustomerSearch(e.target.value)} 
                  onFocus={() => (customerSearchTerm.length > 0 || filteredCustomers.length > 0) && setShowCustomerSuggestions(true)}
                  style={{ border: 'none', padding: '10px', outline: 'none', width: '100%', fontSize: '14px' }}
                />
                {customerSearchTerm && (
                  <button onClick={() => {
                    setSelectedCustomer(null);
                    setCustomerSearchTerm('');
                    setFormData(prev => ({
                      ...prev,
                      customerName: '',
                      customerID: '',
                      shippingAddressId: '',
                      shippingAddress: '',
                      shippingPersonName: '',
                      shippingPersonPhone: '',
                      shippingPersonEmail: '',
                      shippingSnapshot: null
                    }));
                  }} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                    <X size={16} color="#64748b" />
                  </button>
                )}
              </div>
              {showCustomerSuggestions && (
                <div style={suggestionsBoxStyle}>
                  {filteredCustomers.length > 0 ? (
                    filteredCustomers.map(cust => (
                      <div key={cust.id} onClick={() => selectCustomer(cust)} style={suggestionItemStyle}>
                        <div style={{ fontWeight: 'bold' }}>{cust.display_name}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{cust.company_name}</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#64748b' }}>No customers found</div>
                  )}
                </div>
              )}
            </div>
          ) : isInterbranch ? (
            <div style={{ position: 'relative' }} ref={destWarehouseSearchRef}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '6px' }}>
                <label style={labelStyle}>Destination Warehouse</label>
                {setShowNewWarehouseModal && (
                  <button onClick={() => setShowNewWarehouseModal(true)} style={linkBtnStyle}>
                    <Plus size={14} /> New Warehouse
                  </button>
                )}
              </div>
              <div style={getFieldErrorStyle('customerName', { display: 'flex', alignItems: 'center', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0 10px' })}>
                <Building2 size={18} color="#64748b" />
                <input 
                  type="text" 
                  placeholder="Search destination warehouse..." 
                  value={destWarehouseSearchTerm} 
                  onChange={(e) => handleDestWarehouseSearch(e.target.value)} 
                  onFocus={() => (destWarehouseSearchTerm.length > 0 || filteredDestWarehouses.length > 0) && setShowDestWarehouseSuggestions(true)}
                  style={{ border: 'none', padding: '10px', outline: 'none', width: '100%', fontSize: '14px' }}
                />
                {destWarehouseSearchTerm && (
                  <button onClick={() => { setDestWarehouseSearchTerm(''); setFormData(prev => ({...prev, customerName: '', shippingAddress: '', shippingPersonName: '', shippingPersonPhone: '', shippingPersonEmail: '', destinationWarehouseGstin: ''})); }} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                    <X size={16} color="#64748b" />
                  </button>
                )}
              </div>
              {showDestWarehouseSuggestions && (
                <div style={suggestionsBoxStyle}>
                  {filteredDestWarehouses.length > 0 ? (
                    filteredDestWarehouses.map(wh => (
                      <div key={wh.id} onClick={() => selectDestWarehouse(wh)} style={suggestionItemStyle}>
                        <div style={{ fontWeight: 'bold' }}>{wh.name} ({wh.code})</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{wh.city}, {wh.state}</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#64748b' }}>No warehouses found</div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
                <label style={labelStyle}>Destination Party</label>
                <input 
                    type="text" 
                    placeholder="Enter destination..." 
                    value={formData.customerName}
                    onChange={(e) => {
                      clearValidationError('customerName');
                      setFormData(prev => ({...prev, customerName: e.target.value}));
                    }}
                    style={getFieldErrorStyle('customerName')}
                />
            </div>
          )}

          <div>
            <label style={labelStyle}>{isSalesRental ? 'Customer Delivery Address' : 'Destination Address'}</label>
            {isSalesRental && selectedCustomer && (
              <select 
                onChange={(e) => handleAddressSelect(e.target.value)} 
                style={{ ...inputStyle, marginBottom: '8px' }}
                value={formData.shippingAddressId || ''}
              >
                <option value="">Select Delivery Address...</option>
                {selectedCustomer.addresses?.filter(a => a.address_type === 'Shipping').map(addr => (
                  <option key={addr.id} value={addr.id}>{formatAddress(addr)}</option>
                ))}
                <option value="new">+ Add New Address</option>
              </select>
            )}
            <div style={{ ...inputStyle, minHeight: '80px', backgroundColor: '#f9fafb', color: '#374151', fontSize: '13px', overflowY: 'auto' }}>
              {isSalesRental ? (
                formData.shippingAddress ? (
                  formData.shippingAddress
                ) : (
                  <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                    {selectedCustomer ? 'Select a delivery address to view it here...' : 'Select a customer to view address...'}
                  </span>
                )
              ) : (
                <textarea 
                  name="shippingAddress" 
                  value={formData.shippingAddress} 
                  onChange={(e) => setFormData(prev => ({...prev, shippingAddress: e.target.value}))}
                  placeholder="Address details will appear here..."
                  style={{ width: '100%', height: '100%', border: 'none', background: 'transparent', outline: 'none', resize: 'none', fontFamily: 'inherit' }} 
                />
              )}
            </div>
          </div>

          <div style={{ backgroundColor: '#f0fdf4', padding: '12px', borderRadius: '8px', border: '1px solid #dcfce7', minHeight: '80px' }}>
            <label style={{ ...labelStyle, color: '#166534' }}>Destination Contact Details</label>
            <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ fontWeight: '600', color: '#14532d' }}>
                <input 
                    type="text" 
                    placeholder="Contact Name" 
                    value={formData.shippingPersonName} 
                    onChange={(e) => setFormData(prev => updateSalesShippingSnapshot(prev, { shippingPersonName: e.target.value }))}
                    style={{ background: 'transparent', border: 'none', fontWeight: 'bold', width: '100%', outline: 'none' }}
                />
              </div>
              <div style={{ color: '#166534', display: 'flex', alignItems: 'center', gap: '4px' }}>
                Phone: 
                <input 
                    type="text" 
                    placeholder="Phone" 
                    value={formData.shippingPersonPhone} 
                    onChange={(e) => setFormData(prev => updateSalesShippingSnapshot(prev, { shippingPersonPhone: e.target.value }))}
                    style={{ background: 'transparent', border: 'none', color: 'inherit', width: '100%', outline: 'none' }}
                />
              </div>
              <div style={{ color: '#166534', display: 'flex', alignItems: 'center', gap: '4px' }}>
                Email: 
                <input 
                    type="text" 
                    placeholder="Email" 
                    value={formData.shippingPersonEmail} 
                    onChange={(e) => setFormData(prev => updateSalesShippingSnapshot(prev, { shippingPersonEmail: e.target.value }))}
                    style={{ background: 'transparent', border: 'none', color: 'inherit', width: '100%', outline: 'none' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const cardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const sectionHeaderStyle = { fontSize: '16px', fontWeight: 'bold', borderBottom: '1px solid #f3f4f6', paddingBottom: '10px', marginBottom: '15px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' };
const suggestionsBoxStyle = { position: 'absolute', top: '100%', left: 0, width: '100%', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', marginTop: '4px', zIndex: 10, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' };
const suggestionItemStyle = { padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '13px' };
const linkBtnStyle = { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: 4, padding: 0 };

export default MocDeliveryDetails;
